import { setClickRecord } from '@/api/account';
import Image from '@/components/Image';
import Popover from '@/components/Popover';
import useLogout from '@/hooks/useLogout';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { colors, font } from '@/theme/zeelool.theme';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { Badge, Box } from '@mui/material';
import { AccountOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import styles from './AccountIcon.module.scss';

const AccountIcon = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const pathname = usePathname();
  const t = useTranslations('layout.accountIcon');
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const token = useStore((state) => state.token);
  const userInfo = useStore((state) => state.userInfo);
  const customerAccMark = useStore((state) => state.userInfo.customer_acc_mark);
  const { logout } = useLogout();
  const currency = useStore((state) => state.currency);
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  const router = useRouter();
  const isMobile = useStore((state) => state.isMobileScreen);
  const [accPopoverShow, setAccPopoverShow] = useState(false);

  // 图标颜色类名
  const iconColorClass = isDarkMode ? 'text-white' : 'text-primary-black';

  // Badge公共样式
  const commonBadgeStyles = {
    position: 'absolute',
    boxSizing: 'border-box',
    textAlign: 'center',
    color: '#fff',
    backgroundColor: colors.primaryPurple,
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: font.br,
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    willChange: 'auto',
    '& .MuiBadge-badge.MuiBadge-standard.MuiBadge-invisible': {
      display: 'none',
    },
  };

  // Badge样式配置 - 根据设备类型选择特定样式
  const deviceSpecificStyles = isMobile
    ? {
        // 移动端特定样式
        top: '-1.067vw',
        left: '3.733vw',
        minWidth: '3.733vw',
        height: '3.733vw',
        lineHeight: '3.733vw',
        padding: '0 1.067vw',
        borderRadius: '2.133vw',
        fontSize: '2.933vw',
      }
    : {
        // PC端特定样式
        top: '-8px',
        left: '39px',
        minWidth: '18px',
        height: '18px',
        lineHeight: '18px',
        padding: '0 5px',
        borderRadius: '10px',
        fontSize: '12px',
      };

  // 合并公共样式和设备特定样式
  const badgeStyles = {
    ...commonBadgeStyles,
    ...deviceSpecificStyles,
  };

  // 处理用户名显示
  const handleLongName = (name: string) => {
    if (!name) return '';
    return name.length > 10 ? `${name.substring(0, 10)}...` : name;
  };

  // 菜单项点击事件
  const goToUserCenter = useCallback(
    (route: string) => {
      if (pathname === `/account/${route}`) {
        return;
      }
      router.push(`/account/${route}`);
    },
    [pathname],
  );

  // 账户弹窗点击事件追踪
  const AccountPopUpClick = (btn_name: string) => {
    trackEvent(
      {
        eventName: 'AccountPopUp_Click',
        properties: {
          btn_name,
          url_path: window.location.href,
          page_name: cnName,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  // 登出功能
  const signOut = () => {
    logout();
  };

  useEffect(() => {
    if (accPopoverShow) {
      trackEvent(
        {
          eventName: 'AccountPopUp_Exp',
          properties: {
            page_name: cnName,
            url_path: window.location.href,
          },
        },
        [ProviderType.SENSORS],
      );
    }
  }, [accPopoverShow]);

  // 账户下拉菜单内容
  const AccountDropdownContent = () => (
    <Box className="w-[300px] p-[25px] pb-[24px] bg-white rounded-[0.4167vw] shadow-md">
      {/* 用户信息头部 */}
      <div className="flex items-center text-black text-[18px] mb-[8px] pb-[16px] border-b border-[#eeeeee] font-bold break-all">
        <div className="flex items-center justify-center relative w-[48px] h-[48px] mr-[10px]">
          <Image
            src={
              userInfo.member_level && userInfo.member_level < 0
                ? '/layout/no-member.png'
                : '/layout/member.png'
            }
            alt={t('zeeloolEyeglasses')}
            className="w-[45px] h-[45px] mr-0"
            width={45}
            height={45}
          />
          {/* 如果有等级图标可以在这里添加 */}
          {/* <img className="w-[22px] h-[22px] absolute right-0 bottom-0" src="level-icon" alt="level" /> */}
        </div>
        <span>
          {t('hi')} {handleLongName(userInfo.name || 'Guest')}
        </span>
      </div>

      {/* 菜单列表 */}
      <div className="flex flex-col">
        {/* Refer a Friend */}
        <div
          className="flex items-center justify-between h-[36px] text-black text-[16px] transition-all duration-300 cursor-pointer hover:font-bold hover:bg-[#f5f6f7]"
          onClick={(e) => {
            // 阻止事件冒泡，要不会上升到handleAccountClick这个事件，导致双重触发，下同
            e.stopPropagation();
            goToUserCenter('refer-a-friend');
            AccountPopUpClick('Refer a friend');
          }}
        >
          <div className="flex items-center">
            <span>{t('referAFriend')}</span>
          </div>
          {userInfo?.invite_points?.inviter?.money && (
            <div className={styles.addPointIcon}>
              {formatPriceNumberWithSymbol(userInfo?.invite_points?.inviter?.money, currency)}
            </div>
          )}
        </div>

        {/* 条件显示ZEELOOL Rewards */}
        {userInfo.is_member === 1 && (
          <div
            className="flex items-center h-[36px] text-black text-[16px] transition-all duration-300 cursor-pointer hover:font-bold hover:bg-[#f5f6f7]"
            onClick={(e) => {
              e.stopPropagation();
              goToUserCenter('zeelool-rewards');
              AccountPopUpClick('Zeelool Rewards');
            }}
          >
            {t('zeeloolRewards')}
          </div>
        )}

        {/* My Account */}
        <div
          className="flex items-center h-[36px] text-black text-[16px] transition-all duration-300 cursor-pointer hover:font-bold hover:bg-[#f5f6f7]"
          onClick={(e) => {
            e.stopPropagation();
            goToUserCenter('my-account');
            AccountPopUpClick('My Account');
          }}
        >
          {t('myAccount')}
        </div>

        {/* Account Settings */}
        <div
          className={`flex items-center h-[36px] text-black text-[16px] transition-all duration-300 cursor-pointer hover:font-bold hover:bg-[#f5f6f7]  ${
            userInfo.customer_mark === 1 ? styles.redPoint : ''
          }`}
          onClick={(e) => {
            e.stopPropagation();
            goToUserCenter('account-settings');
            AccountPopUpClick('My Info');
          }}
        >
          <span>{t('accountSettings')}</span>
          {userInfo.is_show_point_tag === 1 && userInfo.perfect_point && (
            <div className={styles.addPointIcon}>
              + {userInfo.perfect_point} {userInfo.perfect_point > 1 ? t('points') : t('point')}
            </div>
          )}
        </div>

        {/* My Orders */}
        <div
          className="flex items-center h-[36px] text-black text-[16px] transition-all duration-300 cursor-pointer hover:font-bold hover:bg-[#f5f6f7]"
          onClick={(e) => {
            e.stopPropagation();
            goToUserCenter('my-orders');
            AccountPopUpClick('My Orders');
          }}
        >
          {t('myOrders')}
        </div>

        {/* My Coupons */}
        <div
          className="flex items-center h-[36px] text-black text-[16px] transition-all duration-300 cursor-pointer hover:font-bold hover:bg-[#f5f6f7]"
          onClick={(e) => {
            e.stopPropagation();
            goToUserCenter('my-coupons');
            AccountPopUpClick('My Coupons');
          }}
        >
          {t('myCoupons')}
        </div>

        {/* Rewards Store */}
        <div
          className="flex items-center justify-between h-[36px] text-black text-[16px] transition-all duration-300 cursor-pointer hover:font-bold hover:bg-[#f5f6f7]"
          onClick={(e) => {
            e.stopPropagation();
            goToUserCenter('points-mall');
            AccountPopUpClick('Points Mall');
          }}
        >
          <div className="flex items-center">
            <span>{t('rewardsStore')}</span>
          </div>
          {userInfo?.invite_points?.invitee_pay?.point && (
            <div className={styles.addPointIcon}>
              + {userInfo?.invite_points?.invitee_pay?.point}{' '}
              {userInfo?.invite_points?.invitee_pay?.point > 1 ? 'Points' : 'Point'}
            </div>
          )}
        </div>

        {/* Sign Out */}
        <div
          className="flex items-center h-[36px] text-black text-[16px] mt-[10px] pt-[10px] border-t border-[#eeeeee] transition-all duration-300 cursor-pointer hover:text-primary-purple"
          onClick={(e) => {
            e.stopPropagation();
            signOut();
          }}
        >
          {t('signOut')}
        </div>
      </div>
    </Box>
  );

  const handleAccountClick = async () => {
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: '我的',
          url_path: window.location.href,
          page_name: cnName, // 使用路由元数据中的中文名称
          tab_index: 3,
        },
      },
      [ProviderType.SENSORS],
    );
    if (token) {
      setClickRecord({
        type: 1,
      });
      if (pathname === '/account/my-account') {
        return;
      }
      router.push('/account/my-account');
    } else {
      setShowLoginRegisterDialog({
        show: true,
        eventType: 'register',
        situation: '导航登录',
        initiative: true,
      });
    }
  };

  return (
    <li
      className={
        isMobile
          ? 'flex justify-center items-center relative h-[6.400vw] w-[6.400vw] mr-[5.333vw]'
          : 'relative'
      }
      onClick={handleAccountClick}
      style={{
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    >
      {/* PC端已登录状态使用Popover，其他情况使用普通图标 */}
      {!isMobile && token ? (
        <Popover
          id="account-dropdown"
          content={<AccountDropdownContent />}
          triggerComponent={
            // 解决弹层往下14px的问题，做了一正一负，抵消之后，既能够让浮层下移符合UI要求，又能够解决图标居中的问题
            <div className="pb-[14px] pl-[24px] -mb-[14px]">
              <AccountOutlined className={`cursor-pointer text-[24px] ${iconColorClass}`} />
            </div>
          }
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          // 竟然这里还可以传px
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          width={300}
          noPadding
          slotProps={{
            transition: {
              onEnter: () => {
                setAccPopoverShow(true);
              },
              onExited: () => {
                setAccPopoverShow(false);
              },
            },
          }}
        />
      ) : (
        <AccountOutlined className={`cursor-pointer text-[24px] ${iconColorClass}`} />
      )}

      {customerAccMark === 1 && (
        <Badge sx={badgeStyles}>
          <span>{customerAccMark}</span>
        </Badge>
      )}
    </li>
  );
};

export default AccountIcon;
